import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Socket } from 'socket.io-client';

import { EEmit, ESocketEvents } from '@enums/api.enum';
import { TripStatus, type ReservationStatus } from '@enums/trips.enum';
import type { RootState } from '@store/store';
import {
	removeDriverFromTrip,
	removeDriverFromTripsList,
	removeVehicleFromTrip,
	removeVehicleFromTripsList,
	updateCurrentTripStatus,
	updateReservationStatus,
	updateTrip,
	type Driver,
	type Trip,
	type Vehicle,
} from '@store/trips/tripsSlice';
import { getSocket, onSocketCreated } from '@utils/socket';

import useApi from './useApi';

export type SocketTripStatusChange = {
	ids: number[];
	newStatus: TripStatus;
};

export type SocketTripUpdate = {
	ids: number[];
	driverId?: Driver['id'] | null;
	vehicleId?: Vehicle['id'] | null;
	assignmentCleared?: boolean;
};

export type SocketReservationStatusChange = {
	ids: number[];
	newStatus: ReservationStatus;
	passengerId?: number;
};

/**
 * Subscribes to live updates for `tripIds`.
 *
 * Rooms are joined from the socket's `connect` event rather than at subscribe time, so subscribing
 * before the connection is up is fine, and a reconnect (which gets a new socket id server-side, so
 * previous room membership is gone) re-joins on its own.
 */
export default function useTripSocket(tripIds?: Trip['id'][]) {
	const data = useSelector((state: RootState) => state.trips.data);
	const currentTripId = useSelector((state: RootState) => state.trips.currentTrip?.id);

	const dataRef = useRef(data);
	const currentTripIdRef = useRef(currentTripId);
	const [socket, setSocket] = useState<Socket | null>(getSocket);
	const { getDriverAndVehicle, updateSingleTrip } = useApi();
	const dispatch = useDispatch();

	const handleChangeStatus = async (ids: number[], newStatus: TripStatus) => {
		if (ids.includes(currentTripIdRef.current as number)) {
			dispatch(updateCurrentTripStatus(newStatus));
		}
		ids.forEach((id) => {
			const index = dataRef.current.findIndex((trip) => trip.id === id);
			if (index > -1) {
				if (newStatus === TripStatus.Canceled || newStatus === TripStatus.Done) {
					updateSingleTrip(id);
				} else {
					const updatedTrip = { ...dataRef.current[index], status: newStatus };
					dispatch(updateTrip(updatedTrip));
				}
			}
		});
	};

	const handleTripUpdateDriver = (ids: number[], driverId?: Driver['id'] | null, vehicleId?: Vehicle['id'] | null) => {
		if (!driverId && !vehicleId) {
			return;
		}

		ids.forEach((id) => {
			getDriverAndVehicle({
				id,
				driverId,
				vehicleId,
			});
		});
	};

	const handleReservationStatusChange = (ids: number[], newStatus: ReservationStatus) => {
		dispatch(updateReservationStatus({ reservationIds: ids, status: newStatus }));
	};

	const handleAssignmentCleared = (ids: number[], driverId?: Driver['id'] | null, vehicleId?: Vehicle['id'] | null) => {
		if (driverId === undefined && vehicleId === undefined) {
			return;
		}

		const isCurrentTrip = ids.includes(currentTripIdRef.current as number);
		if (isCurrentTrip) {
			if (driverId === null) {
				dispatch(removeDriverFromTrip());
			}
			if (vehicleId === null) {
				dispatch(removeVehicleFromTrip());
			}
		}

		ids.forEach((id) => {
			const index = dataRef.current.findIndex((trip) => trip.id === id);

			if (index > -1) {
				if (driverId === null) {
					dispatch(removeDriverFromTripsList({ id }));
				}
				if (vehicleId === null) {
					dispatch(removeVehicleFromTripsList({ id }));
				}
			}
		});
	};

	// Listeners outlive the render that registered them, so they reach the current handlers through
	// a ref rather than closing over stale ones (`updateSingleTrip` reads `flightNumber`, etc.).
	const handlersRef = useRef({ handleChangeStatus, handleTripUpdateDriver, handleReservationStatusChange, handleAssignmentCleared });

	const connectTripsSocket = () => {
		if (!socket) {
			return null;
		}

		socket.connect();
	};

	const disconnectTripsSocket = () => {
		if (!socket) {
			return null;
		}
		socket.off(); // remove all listeners
		socket.disconnect();
	};

	useEffect(() => {
		dataRef.current = data;
		currentTripIdRef.current = currentTripId;
		handlersRef.current = { handleChangeStatus, handleTripUpdateDriver, handleReservationStatusChange, handleAssignmentCleared };
	});

	useEffect(() => onSocketCreated(setSocket), []);

	// Sorted and joined so the effect re-runs on a changed id set, not on a new array identity.
	const roomsKey = tripIds?.length ? [...tripIds].sort((a, b) => a - b).join() : '';

	useEffect(() => {
		if (!socket || !roomsKey) {
			return;
		}

		const trips = roomsKey.split(',').map(Number);

		const joinRooms = () => socket.emit(EEmit.joinRooms, { trips });

		const onConnectError = (error: Error) => console.error('[socket] connection failed', error.message);

		const onTripStatusChange = ({ ids, newStatus }: SocketTripStatusChange) => {
			handlersRef.current.handleChangeStatus(ids, newStatus);
		};

		const onTripUpdate = ({ ids, driverId, vehicleId, assignmentCleared }: SocketTripUpdate) => {
			if (assignmentCleared) {
				handlersRef.current.handleAssignmentCleared(ids, driverId, vehicleId);
			} else {
				handlersRef.current.handleTripUpdateDriver(ids, driverId, vehicleId);
			}
		};

		const onReservationStatusChange = ({ ids, newStatus }: SocketReservationStatusChange) => {
			handlersRef.current.handleReservationStatusChange(ids, newStatus);
		};

		socket.on(ESocketEvents.connect, joinRooms);
		socket.on(ESocketEvents.connectError, onConnectError);
		socket.on(ESocketEvents.tripStatusChange, onTripStatusChange);
		socket.on(ESocketEvents.tripUpdate, onTripUpdate);
		socket.on(ESocketEvents.reservationStatusChange, onReservationStatusChange);

		if (socket.connected) {
			joinRooms();
		} else {
			socket.connect(); // rooms are joined by the `connect` handler above
		}

		return () => {
			socket.off(ESocketEvents.connect, joinRooms);
			socket.off(ESocketEvents.connectError, onConnectError);
			socket.off(ESocketEvents.tripStatusChange, onTripStatusChange);
			socket.off(ESocketEvents.tripUpdate, onTripUpdate);
			socket.off(ESocketEvents.reservationStatusChange, onReservationStatusChange);
		};
	}, [socket, roomsKey]);

	return {
		socket,
		connectTripsSocket,
		disconnectTripsSocket,
	};
}
